import PropTypes from "prop-types";
import { cn } from "@/utils/cn";
import { createRef, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import useProductForm from "@/hooks/useProductForm";
import useSubscriptionTag from "@/hooks/useSubscriptionTag";
import SubscriptionRadio from "../SubscriptionRadio";
import {getCurrentRegion, NON_SUBSCRIPTION_REGIONS} from "@/utils/regions";

const ProductCardForm = ({ product, sectionId, simpleAtc, showLearnMoreButton = true }) => {
  const { t } = useTranslation();
  const isInitialMount = useRef(true);

  const {
    id,
    quantity,
    hasSubscription,
    isActive,
    setSubscriptionActive,
    loading,
    addToCart,
    loadingOTP,
    addToCartOTP,
    currentPrice,
    isForcedOtp,
  } = useProductForm({ product, formId: sectionId });

  const bottomLayerRef = createRef(null);

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  const addToCartLabel = t("action.add_to_cart");

  const { subscriptionTagLabel } = useSubscriptionTag(currentPrice);

  // Hide subscription options for non-subscription regions
  const region = getCurrentRegion();
  const shouldHideSubscription = region && NON_SUBSCRIPTION_REGIONS.includes(region);
  const showSubscription = hasSubscription && !shouldHideSubscription;

  return (
    <>
      {/* desktop only */}
      <div className="hidden sm:block mb-[1px]">
        {product.available ? (
          showSubscription && !simpleAtc ? (
            <form>
              <div className="flex flex-col px-0 gap-2">
                <input
                  className="sr-only"
                  type="radio"
                  name="id"
                  value={id}
                  readOnly
                  aria-label="Product ID"
                />
                <input
                  className="sr-only"
                  id={`quantity-${product.id}-${sectionId}`}
                  type="number"
                  value={quantity}
                  readOnly
                  aria-label="Quantity"
                />

                <SwitchTransition>
                  <CSSTransition
                    in={true}
                    nodeRef={bottomLayerRef}
                    timeout={isInitialMount.current && isForcedOtp ? 0 : 150}
                    classNames={isInitialMount.current && isForcedOtp ? "" : "fade"}
                    key={isActive}
                    appear={false}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="top-layer flex flex-col text-left gap-1">
                        <SubscriptionRadio
                          id={`otp-${product.id}`}
                          name="subscription-selector"
                          checked={isActive}
                          onValueChange={() => setSubscriptionActive(true)}
                          label={t("product_card.option_sub")}
                          {...(subscriptionTagLabel && {
                            tag: t("product_card.subscription_tag", {
                              percentage: subscriptionTagLabel,
                            }),
                          })}
                        />
                        <SubscriptionRadio
                          id={`otp-${product.id}`}
                          name="subscription-selector"
                          checked={!isActive}
                          onValueChange={() => setSubscriptionActive(false)}
                          label={t("product_card.option_otp")}
                        />
                      </div>

                      <button
                        className={cn("button button--primary", loading && "disabled")}
                        onClick={e => {
                          e.preventDefault();
                          addToCart();
                        }}
                        disabled={loading}
                      >
                        {addToCartLabel}
                      </button>
                    </div>
                  </CSSTransition>
                </SwitchTransition>
                {showLearnMoreButton &&
                  <a href={`/products/${product.handle}`} className="button button--secondary w-full">
                  {t("action.learn_more")}
                </a>
                }
              </div>
            </form>
          ) : (
            // if simpleAtc is true, display the buttons when the product card is hovered on screens only
            <div className={cn("flex flex-col gap-3", simpleAtc && "opacity-0 group-hover:opacity-100 transition-opacity duration-300")}>
              <button
                className={cn("button button--primary w-full", loadingOTP && "disabled")}
                onClick={e => {
                  e.preventDefault();
                  addToCartOTP();
                }}
                disabled={loadingOTP}
              >
                {addToCartLabel}
              </button>
              {showLearnMoreButton &&
                <a href={`/products/${product.handle}`} className="button button--secondary w-full">
                {t("action.learn_more")}
              </a>
              }
            </div>
          )
        ) : (
          <a
            href={`/products/${product.handle}`}
            className={cn("button button--primary w-full")}
          >
            {t("action.learn_more")}
          </a>
        )}
      </div>
      {/* mobile only */}
      <div className="flex flex-col gap-2 sm:hidden mt-1 mb-[1px]">
        {/* Unavailable: show view-only CTA */}
        {!product.available && (
          <a
            href={`/products/${product.handle}`}
            className={cn("mobile-product-card-button button button--primary w-full")}
          >
            {t("action.learn_more")}
          </a>
        )}

        {/* Subscription flow: show subscription/OTP radios + add to cart */}
        {product.available && showSubscription && !simpleAtc && (
          <form>
            <div className="flex flex-col gap-3">
              <input
                className="sr-only"
                type="radio"
                name="id"
                value={id}
                readOnly
                aria-label="Product ID"
              />
              <input
                className="sr-only"
                id={`quantity-${product.id}-${sectionId}-mobile`}
                type="number"
                value={quantity}
                readOnly
                aria-label="Quantity"
              />
              <div className="top-layer flex flex-col text-left gap-1.5">
                <SubscriptionRadio
                  id={`otp-${product.id}-mobile`}
                  name="subscription-selector-mobile"
                  checked={isActive}
                  onValueChange={() => setSubscriptionActive(true)}
                  label={t("product_card.option_sub")}
                  {...(subscriptionTagLabel && {
                    tag: t("product_card.subscription_tag", {
                      percentage: subscriptionTagLabel,
                    }),
                  })}
                />
                <SubscriptionRadio
                  id={`otp-${product.id}-mobile`}
                  name="subscription-selector-mobile"
                  checked={!isActive}
                  onValueChange={() => setSubscriptionActive(false)}
                  label={t("product_card.option_otp")}
                />
              </div>
              <button
                className={cn("mobile-product-card-button button button--primary w-full", loading && "disabled")}
                onClick={e => {
                  e.preventDefault();
                  addToCart();
                }}
                disabled={loading}
              >
                {addToCartLabel}
              </button>
            </div>
          </form>
        )}

        {/* Non-subscription / simple ATC: quick add-to-cart + Learn More */}
        {product.available && !(showSubscription && !simpleAtc) && (
          <div className={cn("flex flex-col gap-3", simpleAtc && "opacity-100")}>
            <button
              className={cn("mobile-product-card-button button button--primary w-full", loadingOTP && "disabled")}
              onClick={e => {
                e.preventDefault();
                addToCartOTP();
              }}
              disabled={loadingOTP}
            >
              {addToCartLabel}
            </button>
            {showLearnMoreButton &&
              <a href={`/products/${product.handle}`} className="mobile-product-card-button button button--secondary w-full">
                {t("action.learn_more")}
              </a>
            }
          </div>
        )}

        {/* Extra Learn More for subscription flow (not shown inside form) */}
        {showLearnMoreButton && showSubscription && !simpleAtc && (
          <a href={`/products/${product.handle}`} className="mobile-product-card-button button button--secondary w-full">
            {t("action.learn_more")}
          </a>
        )}
      </div>
    </>
  );
};

ProductCardForm.propTypes = {
  product: PropTypes.object.isRequired,
  sectionId: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default ProductCardForm;